import React from "react";
import {useAppDispatch, useAppSelector} from "../../../redux/redux.hook";
import {createSelector} from "@reduxjs/toolkit";
import {CreateAvailabilityData, FeAvailability, UpdateAvailabilityInput} from "./types";
import {getDate, getMonth, getYear} from "date-fns";
import {shallowEqual} from "react-redux";
import {
    createAvailabilityThunk, getAvailabilityThunk,
    listAvailabilitiesThunk,
    listWorkerAvailabilities,
    updateAvailabilityThunk
} from "./availabilities.thunk";
import {selectAllAvailabilities, selectAvailabilityById} from "./availabilities.slice";

export function useWorkerAvailabilities(workerId: string, year: number, month: number, daysInMonth: number) {

    const [loading, setLoading] = React.useState(false);

    const dispatch = useAppDispatch();

    const {daysWithAvailabilities, orders} = useAppSelector(
        createSelector(
            selectAllAvailabilities,
            () => [...Array(daysInMonth)] as (FeAvailability | undefined)[],
            (os, allDays) => {
                const days = [...allDays];
                const workerAvailabilities: FeAvailability[] = []
                let amountWorkerAvailabilities = 0;
                os.forEach(o => {
                    const orderMonth = getMonth(o.from);
                    const orderYear = getYear(o.from);
                    if (o.workerId === workerId && orderMonth === month && orderYear === year) {
                        workerAvailabilities.push(o);
                        days[getDate(o.from) - 1] = o;
                        amountWorkerAvailabilities = amountWorkerAvailabilities + 1;
                    }
                })
                return {
                    daysWithAvailabilities: days,
                    orders: workerAvailabilities
                };
            }
        ))

    React.useEffect(() => {
        setLoading(true);
        dispatch(listWorkerAvailabilities({workerId}))
            .catch(() => {
            })
            .finally(() => setLoading(false));
    }, [workerId, dispatch]);

    return {orders, daysWithAvailabilities, loading};
}

export function useAvailabilities() {
    const dispatch = useAppDispatch();
    const orders = useAppSelector(selectAllAvailabilities, shallowEqual);

    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        setLoading(true);
        dispatch(listAvailabilitiesThunk())
            .catch(() => {
            })
            .finally(() => setLoading(false));
    }, [dispatch]);

    return {orders, loading};
}

export function useCreateAvailability() {
    const dispatch = useAppDispatch();
    return {createAvailability: (dto: CreateAvailabilityData) => dispatch(createAvailabilityThunk(dto))};
}

export function useUpdateAvailability() {
    const dispatch = useAppDispatch();
    return {
        updateAvailability: (id: string, dto: UpdateAvailabilityInput) => dispatch(updateAvailabilityThunk({
            id,
            dto
        }))
    };
}

export function useAvailability(id: string) {
    const dispatch = useAppDispatch();

    const order = useAppSelector((state) => selectAvailabilityById(state, id));
    const error = useAppSelector((state) => state.orders.errors[id]);

    React.useEffect(() => {
        if (!order && !error) {
            dispatch(getAvailabilityThunk({id}))
        }
    }, [dispatch, error, id, order])

    return {
        order,
        error
    }
}
